import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { Container, Menu } from './styles';

import Logo from '~/assets/logos/Logo';
import whatsAppSVG from '~/assets/icons/whatsapp.svg';

const getHeaderHeight = () => (window.innerWidth >= 768 ? 116 : 60);

const isDarkColor = (value: string): boolean | null => {
  const match = value.match(/rgba?\(([^)]+)\)/);
  if (!match) return null;

  const [r, g, b, a = 1] = match[1]
    .split(/[\s,/]+/)
    .filter(Boolean)
    .map(Number);

  // Fundo praticamente transparente: segue procurando o que está atrás
  if (a < 0.5) return null;

  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
};

// Descobre se o conteúdo que está atrás do menu, naquele ponto, é escuro.
// Pode ser forçado em qualquer elemento com data-header-theme="dark|light".
const isDarkAt = (x: number, y: number): boolean => {
  const header = document.getElementById('menu');
  const stack = document.elementsFromPoint(x, y);

  for (let i = 0; i < stack.length; i += 1) {
    const el = stack[i];

    if (!header || !header.contains(el)) {
      const themed = el.closest<HTMLElement>('[data-header-theme]');
      if (themed) return themed.dataset.headerTheme === 'dark';

      // Imagens e vídeos de capa são tratados como fundo escuro
      if (el instanceof HTMLImageElement || el instanceof HTMLVideoElement) {
        return true;
      }

      const style = window.getComputedStyle(el);
      if (style.backgroundImage !== 'none') return true;

      const dark = isDarkColor(style.backgroundColor);
      if (dark !== null) return dark;
    }
  }

  return false;
};

const isDarkBackdrop = (): boolean => {
  const y = getHeaderHeight() / 2;
  const votes = [0.2, 0.5, 0.8].map((ratio) =>
    isDarkAt(window.innerWidth * ratio, y)
  );

  return votes.filter(Boolean).length >= 2;
};

const Header: React.FC = () => {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [height, setHeight] = useState(window.innerHeight);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [darkBackdrop, setDarkBackdrop] = useState(false);
  const frame = useRef(0);

  useEffect(() => {
    if (location.pathname.includes('rede-social')) {
      setWhatsappMessage(
        'Olá! Encontrei vocês nas redes sociais e estou interessado em marcar um horário.'
      );
    } else {
      setWhatsappMessage('Olá gostaria de agendar uma consulta');
    }
  }, [location.pathname]);

  const updateTheme = useCallback(() => {
    window.cancelAnimationFrame(frame.current);
    frame.current = window.requestAnimationFrame(() => {
      setDarkBackdrop(isDarkBackdrop());
    });
  }, []);

  const handleScroll = useCallback(() => {
    setScrollPosition(window.scrollY);
    updateTheme();
  }, [updateTheme]);

  // Menu branco sobre fundo escuro, preto sobre fundo claro
  const isLight = darkBackdrop && !active;

  useEffect(() => {
    window.addEventListener('resize', () => {
      setHeight(window.innerHeight);
    });
  }, []);

  useEffect(() => {
    handleScroll();
    // Imagens e seções podem terminar de carregar depois da troca de rota
    const timers = [300, 1000, 2500].map((delay) =>
      window.setTimeout(updateTheme, delay)
    );

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateTheme);
    window.addEventListener('load', updateTheme);
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.cancelAnimationFrame(frame.current);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateTheme);
      window.removeEventListener('load', updateTheme);
    };
  }, [handleScroll, updateTheme, location.pathname]);

  useEffect(() => {
    setActive(false);
  }, [location]);

  return (
    <>
      <Container scrollPosition={scrollPosition} isLight={isLight}>
        <div
          id="menu"
          className="container-fluid py-2 py-md-0 menu-fix d-flex align-items-center"
        >
          <div className="row w-100 mx-0">
            <div className="col-12">
              <div className="container px-0 ps-sm-3">
                <div className="row">
                  <div className="col-12 d-flex justify-content-between align-items-center text-center">
                    <div>
                      <NavLink
                        to="/"
                        className="d-flex align-items-end mt-n3"
                        title="Página Inicial"
                      >
                        <Logo
                          color={isLight ? '#fff' : '#202020'}
                          className="me-2 logo"
                        />
                        <span className="text-start d-block fs-6 fs-lg-4 fw-light mb-0 logo-name">
                          Prime Odontologia
                        </span>
                      </NavLink>
                    </div>
                    <Menu active={active} height={height} isLight={isLight}>
                      <button
                        type="button"
                        className="bg-transparent d-lg-none border-0 mx-3"
                        onClick={() => setActive(!active)}
                        title="Menu"
                      >
                        <span />
                        <span />
                        <span />
                      </button>
                      <div className="menu-group active">
                        <div className="menu d-flex flex-column flex-lg-row justify-content-between justify-content-lg-center align-items-center">
                          <div className="d-flex flex-column flex-lg-row justify-content-center mt-5 pt-5 mt-lg-0 pt-lg-0">
                            <NavLink
                              to={`${process.env.PUBLIC_URL}/`}
                              className="btn-menu mb-5 mb-lg-0 mx-4 mx-lg-2 mx-xl-3 d-flex justify-content-center align-items-center"
                              exact
                              rel="canonical"
                              activeClassName="active"
                              title="Página Inicial"
                            >
                              Início
                            </NavLink>
                            <NavLink
                              to={`${process.env.PUBLIC_URL}/blog`}
                              className="btn-menu mb-5 mb-lg-0 mx-4 mx-lg-2 mx-xl-3 d-flex justify-content-center align-items-center"
                              exact
                              activeClassName="active"
                              title="Blog"
                            >
                              Blog
                            </NavLink>
                            {/* <NavLink
                              to={`${process.env.PUBLIC_URL}/implantes`}
                              className="btn-menu mb-5 mb-lg-0 mx-4 mx-lg-2 mx-xl-3 d-flex justify-content-center align-items-center"
                              exact
                              activeClassName="active"
                              title="Implantes"
                            >
                              Implantes
                            </NavLink>
                            <NavLink
                              to={`${process.env.PUBLIC_URL}/estetica`}
                              className="btn-menu mb-5 mb-lg-0 mx-4 mx-lg-2 mx-xl-3 d-flex justify-content-center align-items-center"
                              exact
                              activeClassName="active"
                              title="Implantes"
                            >
                              Estética
                            </NavLink> */}
                            <NavLink
                              to={`${process.env.PUBLIC_URL}/contato`}
                              className="btn-menu mb-5 mb-lg-0 mx-4 mx-lg-2 mx-xl-3 d-flex justify-content-center align-items-center"
                              activeClassName="active"
                              title="Contato"
                            >
                              Contato
                            </NavLink>
                          </div>
                          <a
                            href={`https://api.whatsapp.com/send?phone=551141981987&text=${whatsappMessage}`}
                            rel="noopener noreferrer"
                            target="_blank"
                            className="btn schedule text-white w-100 mb-n3 mb-lg-0 mt-lg-n3 ms-lg-3 px-lg-4 d-flex align-items-center justify-content-center"
                            title="Agendar consulta"
                            id="appointment_header"
                          >
                            <span>Agendar consulta</span>&nbsp;&nbsp;
                            <img src={whatsAppSVG} width={26} alt="whatsapp" />
                          </a>
                        </div>
                      </div>
                    </Menu>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default Header;
